/** Colour + short code per source site.
 *
 *  The design prototype hardcoded nine sites and fell back to grey #444 for
 *  anything else. Real sites come from scraper config and change over time, so
 *  unknown names get a deterministic hue derived from the name instead — stable
 *  across reloads, and visually distinct without needing a curated entry. */

export interface SiteMeta {
  color: string;
  short: string;
}

const KNOWN: Record<string, SiteMeta> = {
  'Nike SNKRS': { color: '#8a8a99', short: 'SNK' },
  'Nike AU': { color: '#8a8a99', short: 'NKE' },
  'Culture Kings': { color: '#c05cff', short: 'CK' },
  'Above The Clouds': { color: '#7a3aff', short: 'ATC' },
  Finesse: { color: '#d4af37', short: 'FIN' },
  'Sneaker LAH': { color: '#22c1e4', short: 'SLH' },
  'Foot Locker': { color: '#ff5400', short: 'FL' },
  'JB Hi-Fi': { color: '#f5b942', short: 'JB' },
  Facebook: { color: '#1877f2', short: 'FB' },
  Instagram: { color: '#e1306c', short: 'IG' },
  INCU: { color: '#4ac6a0', short: 'INC' },
  'Up There Store': { color: '#5b62ff', short: 'UP' },
  'Fast Times': { color: '#ff8a4c', short: 'FST' },
  Pokebox: { color: '#ffcb05', short: 'PKB' },
  LootVault: { color: '#2ee07a', short: 'LV' },
  Toymate: { color: '#e879f9', short: 'TOY' },
};

/** Short code from the site name: initials for multi-word, first 3 letters
 *  otherwise. "Above The Clouds" -> "ATC", "Salomon" -> "SAL". */
function deriveShort(name: string): string {
  const words = name.trim().split(/[\s\-_]+/).filter(Boolean);
  if (words.length === 0) return '—';
  if (words.length === 1) return words[0]!.slice(0, 3).toUpperCase();
  return words
    .slice(0, 3)
    .map((w) => w[0]!)
    .join('')
    .toUpperCase();
}

/** FNV-1a, so the same site always lands on the same hue. */
function hueFor(name: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    hash ^= name.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return Math.abs(hash) % 360;
}

export function siteMeta(name: string | null | undefined): SiteMeta {
  if (!name) return { color: 'var(--surface-3)', short: '—' };
  const known = KNOWN[name];
  if (known) return known;
  // Fixed saturation/lightness keeps generated colours legible against the
  // dark surfaces and consistent with the curated palette above.
  return { color: `hsl(${hueFor(name)} 58% 52%)`, short: deriveShort(name) };
}

/** Badge backgrounds need contrast against --elev-1; near-black site colours
 *  (several brands are #111 / #101010) would disappear. */
export function badgeColor(name: string | null | undefined): string {
  const { color } = siteMeta(name);
  return color === '#111' || color === '#101010' || color === '#000000'
    ? 'var(--surface-3)'
    : color;
}
